/**
 * User Role Definitions for ModularCompliance RBAC System
 */

/**
 * User roles enumeration
 */
export enum UserRole {
  ADMIN = 'Administrator',
  COMPLIANCE_OFFICER = 'Compliance Officer',
  RISK_SPECIALIST = 'Risk & Control Specialist',
  IMPLEMENTATION_OFFICER = 'Implementation Officer',
  DEPARTMENT_MANAGER = 'Department Manager',
  TASK_IMPLEMENTER = 'Task Implementer',
  AUDITOR = 'Auditor',
  EXECUTIVE = 'Executive',
  DOCUMENT_MANAGER = 'Document Manager',
  EXTERNAL_CONSULTANT = 'External Consultant',
  ALL = 'ALL' // Special role for items visible to all users
}

/**
 * Permission types for CRUD operations
 */
export enum Permission {
  // Framework permissions
  FRAMEWORK_VIEW = 'framework:view',
  FRAMEWORK_CREATE = 'framework:create',
  FRAMEWORK_EDIT = 'framework:edit',
  FRAMEWORK_DELETE = 'framework:delete',
  
  // Obligation permissions
  OBLIGATION_VIEW = 'obligation:view',
  OBLIGATION_CREATE = 'obligation:create',
  OBLIGATION_EDIT = 'obligation:edit',
  OBLIGATION_DELETE = 'obligation:delete',
  
  // Control permissions
  CONTROL_VIEW = 'control:view',
  CONTROL_CREATE = 'control:create',
  CONTROL_EDIT = 'control:edit',
  CONTROL_DELETE = 'control:delete',
  
  // Requirement permissions
  REQUIREMENT_VIEW = 'requirement:view',
  REQUIREMENT_CREATE = 'requirement:create',
  REQUIREMENT_EDIT = 'requirement:edit',
  REQUIREMENT_DELETE = 'requirement:delete',
  
  // Task permissions
  TASK_VIEW = 'task:view',
  TASK_VIEW_OWN = 'task:view_own',
  TASK_CREATE = 'task:create',
  TASK_EDIT = 'task:edit',
  TASK_EDIT_OWN = 'task:edit_own',
  TASK_DELETE = 'task:delete',
  
  // Document permissions
  DOCUMENT_VIEW = 'document:view',
  DOCUMENT_CREATE = 'document:create',
  DOCUMENT_EDIT = 'document:edit',
  DOCUMENT_DELETE = 'document:delete',
  
  // User management permissions
  USER_VIEW = 'user:view',
  USER_CREATE = 'user:create',
  USER_EDIT = 'user:edit',
  USER_DELETE = 'user:delete',
  
  // Settings permissions
  SETTINGS_VIEW = 'settings:view',
  SETTINGS_EDIT = 'settings:edit',
  
  // Report permissions
  REPORT_VIEW = 'report:view',
  REPORT_CREATE = 'report:create',
  REPORT_EXPORT = 'report:export',
  
  // Audit permissions
  AUDIT_VIEW = 'audit:view',
  
  // Analytics permissions
  ANALYTICS_VIEW = 'analytics:view',
  
  // Executive dashboard permission
  EXECUTIVE_DASHBOARD = 'executive:dashboard',
  
  // Team management permissions
  TEAM_VIEW = 'team:view',
  TEAM_MANAGE = 'team:manage'
}

/**
 * Menu item interface
 */
export interface MenuItem {
  label: string;
  path: string;
  icon?: React.ComponentType;
  allowedRoles: UserRole[];
  subItems?: MenuItem[];
}

/**
 * Role-Permission Matrix
 */
export const ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
  [UserRole.ADMIN]: [
    // Admin has all permissions
    ...Object.values(Permission)
  ],
  
  [UserRole.COMPLIANCE_OFFICER]: [
    // Frameworks
    Permission.FRAMEWORK_VIEW,
    Permission.FRAMEWORK_CREATE,
    Permission.FRAMEWORK_EDIT,
    // Obligations
    Permission.OBLIGATION_VIEW,
    Permission.OBLIGATION_CREATE,
    Permission.OBLIGATION_EDIT,
    // Controls
    Permission.CONTROL_VIEW,
    Permission.CONTROL_CREATE,
    Permission.CONTROL_EDIT,
    // Requirements
    Permission.REQUIREMENT_VIEW,
    Permission.REQUIREMENT_CREATE,
    Permission.REQUIREMENT_EDIT,
    // Tasks
    Permission.TASK_VIEW,
    Permission.TASK_CREATE,
    Permission.TASK_EDIT,
    // Documents
    Permission.DOCUMENT_VIEW,
    Permission.DOCUMENT_CREATE,
    Permission.DOCUMENT_EDIT,
    // Reports
    Permission.REPORT_VIEW,
    Permission.REPORT_CREATE,
    Permission.REPORT_EXPORT
  ],
  
  [UserRole.RISK_SPECIALIST]: [
    // Controls (full access)
    Permission.CONTROL_VIEW,
    Permission.CONTROL_CREATE,
    Permission.CONTROL_EDIT,
    // Requirements (full access)
    Permission.REQUIREMENT_VIEW,
    Permission.REQUIREMENT_CREATE,
    Permission.REQUIREMENT_EDIT,
    // Tasks
    Permission.TASK_VIEW,
    Permission.TASK_CREATE,
    Permission.TASK_EDIT,
    // Documents
    Permission.DOCUMENT_VIEW,
    Permission.DOCUMENT_CREATE,
    Permission.DOCUMENT_EDIT,
    // View-only for frameworks and obligations
    Permission.FRAMEWORK_VIEW,
    Permission.OBLIGATION_VIEW
  ],
  
  [UserRole.IMPLEMENTATION_OFFICER]: [
    // Requirements (full access)
    Permission.REQUIREMENT_VIEW,
    Permission.REQUIREMENT_CREATE,
    Permission.REQUIREMENT_EDIT,
    // Tasks (full access)
    Permission.TASK_VIEW,
    Permission.TASK_CREATE,
    Permission.TASK_EDIT,
    // Documents
    Permission.DOCUMENT_VIEW,
    Permission.DOCUMENT_CREATE,
    Permission.DOCUMENT_EDIT,
    // View-only for frameworks, obligations, controls
    Permission.FRAMEWORK_VIEW,
    Permission.OBLIGATION_VIEW,
    Permission.CONTROL_VIEW
  ],
  
  [UserRole.DEPARTMENT_MANAGER]: [
    // Tasks (view all, manage team tasks)
    Permission.TASK_VIEW,
    Permission.TASK_CREATE,
    Permission.TASK_EDIT,
    // Team management
    Permission.TEAM_VIEW,
    Permission.TEAM_MANAGE,
    // Reports
    Permission.REPORT_VIEW,
    Permission.REPORT_CREATE,
    // Documents (view only)
    Permission.DOCUMENT_VIEW
  ],
  
  [UserRole.TASK_IMPLEMENTER]: [
    // Tasks (own tasks only)
    Permission.TASK_VIEW_OWN,
    Permission.TASK_EDIT_OWN,
    // Documents
    Permission.DOCUMENT_VIEW,
    Permission.DOCUMENT_CREATE
  ],
  
  [UserRole.AUDITOR]: [
    // Read-only access to everything
    Permission.FRAMEWORK_VIEW,
    Permission.OBLIGATION_VIEW,
    Permission.CONTROL_VIEW,
    Permission.REQUIREMENT_VIEW,
    Permission.TASK_VIEW,
    Permission.DOCUMENT_VIEW,
    Permission.AUDIT_VIEW,
    Permission.REPORT_VIEW
  ],
  
  [UserRole.EXECUTIVE]: [
    // Executive dashboard
    Permission.EXECUTIVE_DASHBOARD,
    // Reports and analytics
    Permission.REPORT_VIEW,
    Permission.ANALYTICS_VIEW,
    // High-level view permissions
    Permission.FRAMEWORK_VIEW
  ],
  
  [UserRole.DOCUMENT_MANAGER]: [
    // Full document management
    Permission.DOCUMENT_VIEW,
    Permission.DOCUMENT_CREATE,
    Permission.DOCUMENT_EDIT,
    Permission.DOCUMENT_DELETE,
    // View permissions for context
    Permission.FRAMEWORK_VIEW,
    Permission.OBLIGATION_VIEW,
    Permission.CONTROL_VIEW,
    Permission.REQUIREMENT_VIEW,
    Permission.TASK_VIEW
  ],
  
  [UserRole.EXTERNAL_CONSULTANT]: [
    // Limited view access to assigned items only
    Permission.FRAMEWORK_VIEW,
    Permission.DOCUMENT_VIEW,
    // Can create documents
    Permission.DOCUMENT_CREATE
  ],
  
  [UserRole.ALL]: [] // No specific permissions, used for menu items visible to all
};

/**
 * Check if a role has a specific permission
 */
export function hasPermission(role: UserRole, permission: Permission): boolean {
  return ROLE_PERMISSIONS[role]?.includes(permission) || false;
}

/**
 * Check if any of the roles has a specific permission
 */
export function hasAnyRolePermission(roles: UserRole[], permission: Permission): boolean {
  return roles.some(role => hasPermission(role, permission));
}
